import React from "react";
import ReactDOM from "react-dom/client";

import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";

import { ORBIS_BASE_PATH } from "./config/orbisConfig";
import "./styles/orbisCommercial.css";
import "./index.css";
import ProtectedRoute from "./components/ProtectedRoute";
import MainLayout from "./layout/MainLayout";

import MyWorkPage from "./pages/MyWorkPage";
import MyTaskDetailPage from "./pages/MyTaskDetailPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectTemplateLibraryPage from "./pages/ProjectTemplateLibraryPage";
import DashboardExecutivePage from "./pages/DashboardExecutivePage";
import RoadmapPage from "./pages/RoadmapPage";
import GanttPage from "./pages/GanttPage";
import KanbanPage from "./pages/KanbanPage";
import EvidenceMapPage from "./pages/EvidenceMapPage";
import ProductivityPage from "./pages/ProductivityPage";
import DecisionCenterPage from "./pages/DecisionCenterPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ProjectFormPage from "./pages/ProjectFormPage";
import DashboardPage from "./pages/DashboardPage";
import DashboardCommercialCRMPage from "./pages/DashboardCommercialCRMPage";
import CampaignsCommercialPage from "./pages/CampaignsCommercialPage";
import CampaignCommercialDetailPage from "./pages/CampaignCommercialDetailPage";
import CRMQualityPage from "./pages/CRMQualityPage";
import SettingsPage from "./pages/SettingsPage";
import PlatformSettingsPage from "./pages/PlatformSettingsPage";
import LoginPage from "./pages/LoginPage";
import AccountsPage from "./pages/AccountsPage";
import OpportunitiesPage from "./pages/OpportunitiesPage";
import OpportunityDetailPage from "./pages/OpportunityDetailPage";
import AgendaPage from "./pages/AgendaPage";
import AccountDetailPage from "./pages/AccountDetailPage";
import AccountCreatePage from "./pages/AccountCreatePage";
import IndustriesPage from "./pages/IndustriesPage";
import IndustryDetailPage from "./pages/IndustryDetailPage";
import ContactsPage from "./pages/ContactsPage";
import ContactDetailPage from "./pages/ContactDetailPage";
import AffiliationsPage from "./pages/AffiliationsPage";
import AffiliateCompaniesPage from "./pages/AffiliateCompaniesPage";
import AffiliateCompanyDetailPage from "./pages/AffiliateCompanyDetailPage";
import InstitutionPage from "./pages/InstitutionPage";
import UsersPage from "./pages/UsersPage";
import PermissionsPage from "./pages/PermissionsPage";
import DirectoryPage from "./pages/DirectoryPage";
import DirectoryImportPage from "./pages/DirectoryImportPage";
import OrbisDemoRequestsPage from "./pages/OrbisDemoRequestsPage";

const isAuthenticated = () => {
    return !!localStorage.getItem("cani_auth");
};

const getRole = () => localStorage.getItem("role") || "pm";

const getPermissions = () => {
    try {
        const raw =
            localStorage.getItem("cani_permissions") ||
            localStorage.getItem("permissions") ||
            "[]";

        const parsed = JSON.parse(raw);

        if (Array.isArray(parsed)) return parsed;

        if (parsed && typeof parsed === "object") {
            return Object.entries(parsed)
                .filter(([, v]) => Number(v) === 1)
                .map(([k]) => k);
        }

        return [];
    } catch {
        return [];
    }
};

const hasPermission = (permission) => {
    const role = getRole();
    if (role === "admin") return true;
    return getPermissions().includes(permission);
};

const getLandingPath = () => {
    const role = getRole();
    const permissions = getPermissions();

    if (role === "admin" || role === "administrador") return "/dashboardexecutive";

    const has = (key) => permissions.includes(key);

    // Prioridad comercial/institucional para usuarios sin PMO.
    if (has("opportunities.view")) return "/opportunities";
    if (has("crm_dashboard.view")) return "/crm-dashboard";
    if (has("crm_quality.view")) return "/crm-quality";
    if (has("agenda.view")) return "/agenda";
    if (has("accounts.view")) return "/accounts";
    if (has("contacts.view")) return "/contacts";

    if (has("affiliate_companies.view")) return "/affiliate-companies";
    if (has("affiliations.view")) return "/affiliations";
    if (has("institution.view")) return "/institution";
    if (has("directory.view")) return "/directory";

    // PMO / proyectos solo si realmente tiene permisos.
    if (has("projects.view") || has("tasks.view")) return "/my-work";
    if (has("dashboardexecutive.view")) return "/dashboardexecutive";
    if (has("dashboard.view")) return "/dashboard";

    if (has("users.manage") || has("users.view")) return "/users";
    if (has("permissions.manage")) return "/permissions";
    if (has("platform_settings.view")) return "/platform-settings";

    return "/no-access";
};


const NoAccessPage = () => (
    <MainLayout>
        <div className="min-h-screen bg-[#041426] px-4 py-8 text-white">
            <div className="mx-auto max-w-3xl rounded-[28px] border border-[#0b4d78] bg-[#04192b] p-7 shadow-[0_20px_45px_rgba(0,0,0,.28)]">
                <div className="mb-3 inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">
                    <span className="h-px w-10 bg-[#D4AF37]" />
                    Acceso
                </div>
                <h1 className="text-3xl font-black tracking-tight">Sin módulo disponible</h1>
                <p className="mt-3 text-sm leading-relaxed text-[#d7e8f6]">
                    Tu usuario inició sesión correctamente, pero no tiene permisos activos para abrir un módulo inicial.
                    Solicita a un administrador revisar tus permisos.
                </p>
                <div className="mt-5 rounded-2xl border border-[#24587c] bg-[#0b2740] p-4 text-xs text-[#9fc4df]">
                    Recomendación: asignar al menos un permiso como CRM Comercial, Afiliaciones, Institución o PMO.
                </div>
            </div>
        </div>
    </MainLayout>
);

const AccessDeniedPage = ({ permission }) => (
    <MainLayout>
        <div className="min-h-screen bg-[#041426] px-4 py-8 text-white">
            <div className="mx-auto max-w-3xl rounded-[28px] border border-[#0b4d78] bg-[#04192b] p-7 shadow-[0_20px_45px_rgba(0,0,0,.28)]">
                <div className="mb-3 inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">
                    <span className="h-px w-10 bg-[#D4AF37]" />
                    Permisos
                </div>
                <h1 className="text-3xl font-black tracking-tight">Acceso restringido</h1>
                <p className="mt-3 text-sm leading-relaxed text-[#d7e8f6]">
                    Tu usuario no tiene permiso para abrir esta sección.
                </p>
                {permission ? (
                    <code className="mt-5 inline-flex rounded-xl border border-[#24587c] bg-[#0b2740] px-3 py-2 text-xs text-[#9fc4df]">
                        {permission}
                    </code>
                ) : null}
            </div>
        </div>
    </MainLayout>
);

const PermissionRoute = ({ permission, children }) => {
    const location = useLocation();

    if (!isAuthenticated()) {
        return <Navigate to="/login" replace />;
    }

    if (!hasPermission(permission)) {
        const landingPath = getLandingPath();

        // Evita redirecciones circulares cuando el usuario no puede abrir
        // la ruta actual y el landing calculado es la misma ruta.
        if (!landingPath || landingPath === "/no-access") {
            return <NoAccessPage />;
        }

        if (location.pathname === landingPath) {
            return <AccessDeniedPage permission={permission} />;
        }

        return <Navigate to={landingPath} replace />;
    }

    return children;
};

const LandingRedirect = () => {
    if (!isAuthenticated()) {
        return <Navigate to="/login" replace />;
    }

    return <Navigate to={getLandingPath()} replace />;
};

const NoAccessRoute = () => {
    if (!isAuthenticated()) {
        return <Navigate to="/login" replace />;
    }

    return <NoAccessPage />;
};

const AppRoutes = () => (
    <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/no-access" element={<NoAccessRoute />} />
        <Route path="/" element={<LandingRedirect />} />


        <Route
            path="/my-work"
            element={
                <PermissionRoute permission="projects.view">
                    <MainLayout>
                        <MyWorkPage />
                    </MainLayout>
                </PermissionRoute>
            }
        />
        <Route
            path="/my-work/task/:taskId"
            element={
                <PermissionRoute permission="projects.view">
                    <MainLayout>
                        <MyTaskDetailPage />
                    </MainLayout>
                </PermissionRoute>
            }
        />

        <Route
            path="/project-templates"
            element={
                <PermissionRoute permission="projects.view">
                    <ProjectTemplateLibraryPage />
                </PermissionRoute>
            }
        />        <Route
            path="/dashboard"
            element={
                <PermissionRoute permission="dashboard.view">
                    <DashboardPage />
                </PermissionRoute>
            }
        />
        <Route
            path="/dashboardexecutive"
            element={
                <PermissionRoute permission="dashboardexecutive.view">
                    <DashboardExecutivePage />
                </PermissionRoute>
            }
        />
        <Route
            path="/roadmap"
            element={
                <PermissionRoute permission="projects.view">
                    <MainLayout>
                        <RoadmapPage />
                    </MainLayout>
                </PermissionRoute>
            }
        />
        <Route
            path="/gantt"
            element={
                <PermissionRoute permission="projects.view">
                    <MainLayout>
                        <GanttPage />
                    </MainLayout>
                </PermissionRoute>
            }
        />
        <Route
            path="/kanban"
            element={
                <PermissionRoute permission="projects.view">
                    <MainLayout>
                        <KanbanPage />
                    </MainLayout>
                </PermissionRoute>
            }
        />
        <Route
            path="/evidence-map"
            element={
                <PermissionRoute permission="projects.view">
                    <MainLayout>
                        <EvidenceMapPage />
                    </MainLayout>
                </PermissionRoute>
            }
        />
        <Route
            path="/productivity"
            element={
                <PermissionRoute permission="projects.view">
                    <MainLayout>
                        <ProductivityPage />
                    </MainLayout>
                </PermissionRoute>
            }
        />
        <Route
            path="/decision-center"
            element={
                <PermissionRoute permission="projects.view">
                    <MainLayout>
                        <DecisionCenterPage />
                    </MainLayout>
                </PermissionRoute>
            }
        />

        <Route
            path="/institution"
            element={
                <PermissionRoute permission="institution.view">
                    <InstitutionPage />
                </PermissionRoute>
            }
        />
        <Route
            path="/crm-dashboard"
            element={
                <PermissionRoute permission="opportunities.view">
                    <DashboardCommercialCRMPage />
                </PermissionRoute>
            }
        />
        <Route
            path="/crm-quality"
            element={
                <PermissionRoute permission="opportunities.view">
                    <CRMQualityPage />
                </PermissionRoute>
            }
        />
        <Route
            path="/campaigns-commercial"
            element={
                <PermissionRoute permission="opportunities.view">
                    <CampaignsCommercialPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/campaigns-commercial/:id"
            element={
                <PermissionRoute permission="opportunities.view">
                    <CampaignCommercialDetailPage />
                </PermissionRoute>
            }
        />
        <Route
            path="/projects"
            element={
                <PermissionRoute permission="projects.view">
                    <ProjectsPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/projects/:id"
            element={
                <PermissionRoute permission="projects.view">
                    <ProjectDetailPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/projects/new"
            element={
                <PermissionRoute permission="projects.view">
                    <ProjectFormPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/projects/:id/edit"
            element={
                <PermissionRoute permission="projects.view">
                    <ProjectFormPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/accounts"
            element={
                <PermissionRoute permission="accounts.view">
                    <AccountsPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/accounts/new"
            element={
                <PermissionRoute permission="accounts.view">
                    <AccountCreatePage />
                </PermissionRoute>
            }
        />

        <Route
            path="/accounts/:id"
            element={
                <PermissionRoute permission="accounts.view">
                    <AccountDetailPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/industries"
            element={
                <ProtectedRoute>
                    <IndustriesPage />
                </ProtectedRoute>
            }
        />

        <Route
            path="/industries/:id"
            element={
                <ProtectedRoute>
                    <IndustryDetailPage />
                </ProtectedRoute>
            }
        />

        <Route
            path="/opportunities"
            element={
                <PermissionRoute permission="opportunities.view">
                    <OpportunitiesPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/opportunities/new"
            element={
                <PermissionRoute permission="opportunities.view">
                    <OpportunityDetailPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/opportunities/:id"
            element={
                <PermissionRoute permission="opportunities.view">
                    <OpportunityDetailPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/opportunities/:id/edit"
            element={
                <PermissionRoute permission="opportunities.view">
                    <OpportunityDetailPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/agenda"
            element={
                <PermissionRoute permission="agenda.view">
                    <AgendaPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/contacts"
            element={
                <PermissionRoute permission="contacts.view">
                    <ContactsPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/contacts/:id"
            element={
                <PermissionRoute permission="contacts.view">
                    <ContactDetailPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/affiliate-companies"
            element={
                <PermissionRoute permission="affiliate_companies.view">
                    <AffiliateCompaniesPage />
                </PermissionRoute>
            }
        />
        <Route
            path="/affiliate-companies/:id"
            element={
                <PermissionRoute permission="affiliate_companies.view">
                    <AffiliateCompanyDetailPage />
                </PermissionRoute>
            } />
        <Route
            path="/affiliations"
            element={
                <PermissionRoute permission="affiliations.view">
                    <AffiliationsPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/settings"
            element={
                <ProtectedRoute>
                    <SettingsPage />
                </ProtectedRoute>
            }
        />
        <Route
            path="/platform-settings"
            element={
                <PermissionRoute permission="platform_settings.view">
                    <PlatformSettingsPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/users"
            element={
                <PermissionRoute permission="users.manage">
                    <UsersPage />
                </PermissionRoute>
            }
        />
        <Route
            path="/permissions"
            element={
                <PermissionRoute permission="permissions.manage">
                    <PermissionsPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/directory"
            element={
                <PermissionRoute permission="directory.view">
                    <DirectoryPage />
                </PermissionRoute>
            }
        />

        <Route
            path="/directory/import"
            element={
                <PermissionRoute permission="directory.create">
                    <DirectoryImportPage />
                </PermissionRoute>
            }
        />
        <Route
            path="/orbis-demo-requests"
            element={
                <PermissionRoute permission="opportunities.view">
                    <MainLayout>
                        <OrbisDemoRequestsPage />
                    </MainLayout>
                </PermissionRoute>
            }
        />

        <Route path="*" element={<LandingRedirect />} />
    </Routes>
);

const App = () => <AppRoutes />;

ReactDOM.createRoot(document.getElementById("root")).render(
    <React.StrictMode>
        <BrowserRouter basename={ORBIS_BASE_PATH}>
            <App />
        </BrowserRouter>
    </React.StrictMode>
);
